/**
 * Public School Profile Page
 * ==========================
 * Publicly accessible page showcasing school details
 * - Hero Section with Banner & Logo
 * - About Section with Stats
 * - Team Members (Principal, Teachers, Staff)
 * - Call to Action Buttons
 * - Contact Information
 */

import React, { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Feature Card Component
const FeatureCard = ({ icon, title, description }) => (
  <div className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
    <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center text-white text-2xl mb-4 shadow-md">
      {icon === 'book' && '📚'}
      {icon === 'users' && '👥'}
      {icon === 'laptop' && '💻'}
      {icon === 'trophy' && '🏆'}
      {icon === 'shield' && '🛡️'}
      {icon === 'bus' && '🚌'}
    </div>
    <h3 className="font-bold text-lg text-gray-800 mb-2">{title}</h3>
    <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
  </div>
);

// Team Member Card
const TeamMemberCard = ({ member }) => (
  <div className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 group">
    <div className="h-48 bg-gradient-to-br from-orange-100 to-yellow-50 flex items-center justify-center">
      {member.photo_url ? (
        <img 
          src={member.photo_url} 
          alt={member.name} 
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="w-24 h-24 bg-gradient-to-br from-orange-400 to-orange-600 rounded-full flex items-center justify-center text-white text-4xl font-bold shadow-lg">
          {member.name?.charAt(0) || '?'}
        </div>
      )}
    </div>
    <div className="p-5 text-center">
      <h3 className="font-bold text-lg text-gray-800">{member.name}</h3>
      {member.name_hindi && <p className="text-gray-500 text-sm">{member.name_hindi}</p>}
      <p className="text-orange-600 font-medium mt-1">{member.role}</p>
      {member.role_hindi && <p className="text-orange-500 text-sm">{member.role_hindi}</p>}
      {member.qualification && (
        <p className="text-gray-500 text-sm mt-2">📜 {member.qualification}</p>
      )}
      {member.experience && (
        <p className="text-gray-500 text-sm">⏳ {member.experience}</p>
      )}
    </div>
  </div>
);

// CTA Button Component
const CTAButton = ({ cta, schoolPhone }) => {
  const handleClick = () => {
    switch (cta.action_type) {
      case 'phone':
        window.location.href = `tel:${cta.action_value || schoolPhone}`;
        break;
      case 'whatsapp':
        window.open(`https://wa.me/${(cta.action_value || schoolPhone).replace(/[^0-9]/g, '')}`, '_blank');
        break;
      case 'email':
        window.location.href = `mailto:${cta.action_value}`;
        break;
      case 'link':
      case 'admission_form':
        if (cta.action_value.startsWith('http')) {
          window.open(cta.action_value, '_blank');
        }
        break;
      default:
        break;
    }
  };

  return (
    <button
      onClick={handleClick}
      className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 flex items-center gap-2 shadow-md hover:shadow-lg ${
        cta.is_primary 
          ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white hover:from-orange-600 hover:to-orange-700' 
          : 'bg-white text-orange-600 border-2 border-orange-500 hover:bg-orange-50'
      }`}
      data-testid={`cta-${cta.cta_id}`}
    >
      <span className="text-xl">{cta.icon}</span>
      <span>{cta.label}</span>
    </button>
  );
};

// Stat Counter Component
const StatCounter = ({ value, label }) => (
  <div className="text-center">
    <div className="text-4xl font-bold text-white mb-1">{value}</div>
    <div className="text-orange-100 text-sm">{label}</div>
  </div>
);

// Main Component
export default function PublicSchoolProfile({ subdomain }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [language, setLanguage] = useState('hi'); // Default Hindi
  const [selectedImage, setSelectedImage] = useState(null); // For lightbox
  const [activeGalleryCategory, setActiveGalleryCategory] = useState(null); // For gallery filter

  useEffect(() => {
    fetchProfile();
  }, [subdomain]);

  // Share and Download helpers
  const shareToWhatsApp = (image) => {
    const text = `${image.caption || 'School Photo'} - ${profile?.school?.name || ''}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  const downloadImage = async (imageUrl, caption) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${caption || 'school-photo'}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // For base64 images
      const a = document.createElement('a');
      a.href = imageUrl;
      a.download = `${caption || 'school-photo'}.jpg`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };

  const fetchProfile = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/school-profile/public/${subdomain}`);
      const data = await response.json();
      
      if (data.success) {
        setProfile(data.profile);
      } else {
        setError(data.detail || 'School not found');
      }
    } catch (err) {
      console.error('Error fetching profile:', err);
      setError('Failed to load school profile');
    } finally {
      setLoading(false);
    }
  };

  const getText = (en, hi) => language === 'hi' ? (hi || en) : en;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-gray-600">Loading school profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center bg-white p-8 rounded-xl shadow-lg max-w-md">
          <div className="text-6xl mb-4">🏫</div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">School Not Found</h1>
          <p className="text-gray-600 mb-4">विद्यालय नहीं मिला</p>
          <p className="text-sm text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  const school = profile?.school || {};
  const basicInfo = profile?.basic_info || {};
  const contactInfo = profile?.contact_info || {};
  const branding = profile?.branding || {};
  const teamMembers = profile?.team_members || [];
  const ctaButtons = profile?.call_to_actions?.filter(c => c.is_visible) || [];
  const homeSections = profile?.home_sections || [];

  // Extract features from home sections
  const featuresSection = homeSections.find(s => s.section_id === 'features');
  const features = featuresSection?.settings?.features || [];

  // Extract about stats
  const aboutSection = homeSections.find(s => s.section_id === 'about');
  const stats = aboutSection?.settings?.stats || [];

  // Enhanced Gallery data
  const galleryData = profile?.gallery || {};
  const galleryImages = galleryData.images || [];
  const galleryVideos = galleryData.videos || [];
  const featuredImages = galleryData.featured || galleryImages.filter(img => img.is_featured).slice(0, 6);
  const galleryCategories = galleryData.categories || {};

  // Fallback to old structure if no new gallery data
  const oldGallerySection = homeSections.find(s => s.section_id === 'gallery');
  const allGalleryImages = galleryImages.length > 0 ? galleryImages : (oldGallerySection?.settings?.images || []);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="public-school-profile">
      {/* Language Toggle */}
      <div className="fixed top-4 right-4 z-50">
        <button
          onClick={() => setLanguage(language === 'hi' ? 'en' : 'hi')}
          className="px-4 py-2 bg-white rounded-full shadow-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-all"
          data-testid="language-toggle"
        >
          {language === 'hi' ? 'EN' : 'हिंदी'}
        </button>
      </div>

      {/* Hero Section */}
      <header 
        className="relative min-h-[70vh] flex items-center justify-center"
        style={{
          background: profile?.banner_url 
            ? `url(${profile.banner_url}) center/cover no-repeat` 
            : `linear-gradient(135deg, ${branding.primary_color || '#FF6B35'} 0%, ${branding.secondary_color || '#F7931E'} 100%)`
        }}
      >
        <div className="absolute inset-0 bg-black/40"></div>
        
        <div className="relative z-10 text-center px-4 py-12 max-w-4xl mx-auto">
          {/* Logo */}
          {profile?.logo_url && (
            <div className="mb-6 flex justify-center">
              <img 
                src={profile.logo_url} 
                alt={school.name} 
                className="h-24 w-24 rounded-full bg-white p-2 shadow-xl object-contain"
                data-testid="school-logo"
              />
            </div>
          )}
          
          {/* School Name */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 drop-shadow-lg" data-testid="school-name">
            {getText(basicInfo.name, basicInfo.name_hindi)}
          </h1>
          
          {/* Tagline */}
          {basicInfo.tagline && (
            <p className="text-xl md:text-2xl text-orange-100 mb-6 drop-shadow">
              {getText(basicInfo.tagline, basicInfo.tagline_hindi)}
            </p>
          )}
          
          {/* Board & Established */}
          <div className="flex flex-wrap justify-center gap-4 mb-8">
            {basicInfo.affiliation_board && (
              <span className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm">
                📜 {basicInfo.affiliation_board}
              </span>
            )}
            {basicInfo.established_year && (
              <span className="px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm">
                🏛️ Est. {basicInfo.established_year}
              </span>
            )}
          </div>
          
          {/* CTA Buttons */}
          <div className="flex flex-wrap justify-center gap-4">
            {ctaButtons.slice(0, 3).map((cta, index) => (
              <CTAButton key={cta.cta_id || index} cta={cta} schoolPhone={school.phone} />
            ))}
          </div>
        </div>
      </header>

      {/* Stats Section */}
      {(stats.length > 0 || school.student_count || school.teacher_count) && (
        <section className="py-8 bg-gradient-to-r from-orange-500 to-orange-600">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {stats.length > 0 ? (
                stats.map((stat, index) => (
                  <StatCounter 
                    key={index} 
                    value={stat.value} 
                    label={getText(stat.label, stat.label_hindi)} 
                  />
                ))
              ) : (
                <>
                  {school.student_count > 0 && (
                    <StatCounter value={`${school.student_count}+`} label={getText('Students', 'छात्र')} />
                  )}
                  {school.teacher_count > 0 && (
                    <StatCounter value={`${school.teacher_count}+`} label={getText('Teachers', 'शिक्षक')} />
                  )}
                  {basicInfo.established_year && (
                    <StatCounter value={`${new Date().getFullYear() - basicInfo.established_year}+`} label={getText('Years', 'वर्ष')} />
                  )}
                </>
              )}
            </div>
          </div>
        </section>
      )}

      {/* About Section */}
      <section className="py-16 bg-white" data-testid="about-section">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-800 mb-4">
            {getText('About Our School', 'हमारे विद्यालय के बारे में')}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-orange-400 to-orange-600 mx-auto mb-8 rounded-full"></div>
          
          {aboutSection?.content && (
            <p className="text-gray-600 text-lg text-center max-w-3xl mx-auto leading-relaxed">
              {getText(aboutSection.content, aboutSection.content_hindi)}
            </p>
          )}
          
          {/* Principal Info */}
          {school.principal_name && (
            <div className="mt-8 text-center">
              <p className="text-gray-500">
                <span className="font-medium text-gray-700">{getText('Principal', 'प्रधानाचार्य')}:</span>{' '}
                {school.principal_name}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      {features.length > 0 && (
        <section className="py-16 bg-gray-50" data-testid="features-section">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-4">
              {getText(featuresSection?.title || 'Why Choose Us', featuresSection?.title_hindi || 'हमें क्यों चुनें')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-orange-400 to-orange-600 mx-auto mb-12 rounded-full"></div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <FeatureCard
                  key={index}
                  icon={feature.icon}
                  title={getText(feature.title, feature.title_hindi)}
                  description={getText(feature.description, feature.description_hindi)}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Photo Gallery Section - Enhanced */}
      {allGalleryImages.length > 0 && (
        <section className="py-16 bg-white" data-testid="gallery-section">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-4">
              {getText('Photo Gallery', 'फोटो गैलरी')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-orange-400 to-orange-600 mx-auto mb-8 rounded-full"></div>
            
            {/* Featured Photos Slider */}
            {featuredImages.length > 0 && (
              <div className="mb-8">
                <h3 className="text-lg font-medium text-orange-600 mb-4 text-center">⭐ {getText('Featured Photos', 'विशेष फोटोज')}</h3>
                <div className="flex gap-4 overflow-x-auto pb-4 snap-x">
                  {featuredImages.map((img, idx) => (
                    <div 
                      key={img.id || idx} 
                      className="flex-shrink-0 w-64 h-48 rounded-xl overflow-hidden shadow-lg cursor-pointer snap-start"
                      onClick={() => setSelectedImage(img)}
                    >
                      <img src={img.url} alt={img.caption} className="w-full h-full object-cover hover:scale-105 transition-transform" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Category Filter Tabs */}
            {Object.keys(galleryCategories).length > 0 && (
              <div className="flex justify-center gap-2 flex-wrap mb-8">
                <button 
                  onClick={() => setActiveGalleryCategory(null)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${!activeGalleryCategory ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                  {getText('All', 'सभी')}
                </button>
                {Object.entries(galleryCategories).map(([key, cat]) => (
                  <button 
                    key={key}
                    onClick={() => setActiveGalleryCategory(key)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${activeGalleryCategory === key ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                  >
                    {language === 'hi' ? cat.name_hi : cat.name}
                  </button>
                ))}
              </div>
            )}
            
            {/* Photo Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {allGalleryImages
                .filter(img => !activeGalleryCategory || img.category === activeGalleryCategory)
                .map((image, index) => (
                <div 
                  key={image.id || index} 
                  className="relative group overflow-hidden rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer"
                  onClick={() => setSelectedImage(image)}
                >
                  <div className="aspect-square">
                    <img 
                      src={image.url} 
                      alt={image.caption || `Gallery ${index + 1}`}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  {/* Category Badge */}
                  {image.category && galleryCategories[image.category] && (
                    <div className="absolute top-2 left-2 px-2 py-1 bg-black/60 text-white text-xs rounded-full">
                      {galleryCategories[image.category]?.subcategories?.find(s => s.id === image.subcategory)?.icon || '📷'}
                    </div>
                  )}
                  {/* Caption Overlay */}
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <p className="text-white text-sm truncate">
                      {getText(image.caption, image.caption_hindi)}
                    </p>
                    {image.event_date && <p className="text-white/70 text-xs">📅 {image.event_date}</p>}
                  </div>
                  {/* Share Buttons */}
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button 
                      onClick={(e) => { e.stopPropagation(); shareToWhatsApp(image); }}
                      className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center text-sm hover:bg-green-600"
                      title="Share on WhatsApp"
                    >📱</button>
                    <button 
                      onClick={(e) => { e.stopPropagation(); downloadImage(image.url, image.caption); }}
                      className="w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm hover:bg-blue-600"
                      title="Download"
                    >⬇️</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Video Gallery Section */}
      {galleryVideos.length > 0 && (
        <section className="py-16 bg-gray-50" data-testid="video-gallery-section">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-4">
              {getText('Video Gallery', 'वीडियो गैलरी')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-red-400 to-red-600 mx-auto mb-12 rounded-full"></div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {galleryVideos.map((video, index) => (
                <div key={video.id || index} className="bg-white rounded-xl shadow-lg overflow-hidden group">
                  <div className="relative aspect-video">
                    {video.thumbnail ? (
                      <img src={video.thumbnail} alt={video.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-red-100 to-red-200 flex items-center justify-center text-6xl">🎬</div>
                    )}
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                      <a 
                        href={video.video_url} 
                        target="_blank" 
                        rel="noopener noreferrer" 
                        className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center text-white text-2xl hover:bg-red-700 transition-all hover:scale-110"
                      >
                        ▶
                      </a>
                    </div>
                  </div>
                  <div className="p-4">
                    <h4 className="font-bold text-gray-800">{getText(video.title, video.title_hindi)}</h4>
                    {video.description && <p className="text-sm text-gray-500 mt-1">{getText(video.description, video.description_hindi)}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Image Lightbox Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white text-4xl hover:text-orange-400 transition-colors"
            onClick={() => setSelectedImage(null)}
          >
            &times;
          </button>
          <div className="max-w-4xl max-h-[90vh] relative" onClick={e => e.stopPropagation()}>
            <img 
              src={selectedImage.url} 
              alt={selectedImage.caption || 'Gallery Image'}
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
            {(selectedImage.caption || selectedImage.caption_hindi) && (
              <div className="text-center mt-4">
                <p className="text-white text-lg">
                  {getText(selectedImage.caption, selectedImage.caption_hindi)}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Team Members Section */}
      {teamMembers.length > 0 && (
        <section className="py-16 bg-white" data-testid="team-section">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-center text-gray-800 mb-4">
              {getText('Our Team', 'हमारी टीम')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-orange-400 to-orange-600 mx-auto mb-12 rounded-full"></div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {teamMembers.map((member, index) => (
                <TeamMemberCard key={member.member_id || index} member={member} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact Section */}
      <section className="py-16 bg-gradient-to-br from-gray-800 to-gray-900" data-testid="contact-section">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-white mb-4">
            {getText('Contact Us', 'संपर्क करें')}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-orange-400 to-orange-600 mx-auto mb-12 rounded-full"></div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            {/* Address */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center text-3xl mx-auto mb-4">
                📍
              </div>
              <h3 className="text-white font-bold mb-2">{getText('Address', 'पता')}</h3>
              <p className="text-gray-300 text-sm">
                {getText(contactInfo.address, contactInfo.address_hindi)}
                {contactInfo.city && <><br/>{contactInfo.city}</>}
                {contactInfo.state && <>, {contactInfo.state}</>}
                {contactInfo.pincode && <> - {contactInfo.pincode}</>}
              </p>
            </div>
            
            {/* Phone */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center text-3xl mx-auto mb-4">
                📞
              </div>
              <h3 className="text-white font-bold mb-2">{getText('Phone', 'फ़ोन')}</h3>
              <p className="text-gray-300 text-sm">
                {contactInfo.phone || school.phone}
              </p>
            </div>
            
            {/* Email */}
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
              <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center text-3xl mx-auto mb-4">
                ✉️
              </div>
              <h3 className="text-white font-bold mb-2">{getText('Email', 'ईमेल')}</h3>
              <p className="text-gray-300 text-sm">
                {contactInfo.email || school.email}
              </p>
            </div>
          </div>
          
          {/* All CTA Buttons */}
          {ctaButtons.length > 0 && (
            <div className="flex flex-wrap justify-center gap-4 mt-12">
              {ctaButtons.map((cta, index) => (
                <CTAButton key={cta.cta_id || index} cta={cta} schoolPhone={school.phone} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 bg-gray-900 text-center">
        <p className="text-gray-500 text-sm">
          © {new Date().getFullYear()} {school.name}. {getText('All rights reserved.', 'सर्वाधिकार सुरक्षित।')}
        </p>
        <p className="text-gray-600 text-xs mt-2">
          Powered by <span className="text-orange-500 font-medium">शिक्षा मित्र</span>
        </p>
      </footer>
    </div>
  );
}
